/*******************************************
 * [PAA] CART DRAWER (handoff issues #5, #7)
 *
 * <l-cart-drawer> extends the LTA drawer base (A-global.js) the same way
 * the added-to-cart modal did. Behavior:
 *   - Opens on header cart icon click ([data-cart-link], via data-trigger-selector)
 *   - Opens automatically after add-to-cart (`l:product:added`)
 *   - Content refresh is handled by the nested <l-main-cart> (A-main-cart.js)
 *   - Dispatches `l:cart:closed` with the updated variant ids on close
 *     (A-main-product.js already listens for it to re-sync PDP availability)
 *
 * [PAA qa2] Refresh-on-open: the drawer's markup was only ever rendered at
 * page load, so a cart mutated somewhere else (another tab, a Shopify app, a
 * subscription/upsell app writing lines server-side) opened showing STALE
 * contents — wrong lines, wrong subtotal, wrong count. On open we now re-fetch
 * the cart section and hand it to <l-main-cart> to render, with three guards:
 *   1. no fetch when the drawer opens straight off an add — `l:product:added`
 *      already makes l-main-cart fetch fresh HTML, so a 2nd fetch is waste
 *   2. no double-fetch while one refresh is already in flight
 *   3. a response is DISCARDED if the cart was mutated while it was in flight
 *      (quantity change / remove / add), so a stale-by-a-few-hundred-ms body
 *      can never clobber a newer, truer render
 *
 * [PAA qa3] is-loading ownership: the refresh adds `is-loading` to
 * <l-main-cart> and used to remove it unconditionally in .finally(). If a
 * quantity/remove update started while the refresh was in flight, that
 * removal stripped the loading state the NEWER update owned, un-dimming the
 * UI mid-update. The refresh now takes an ownership token when it adds the
 * class; any theme-owned update that starts meanwhile (quantity change,
 * remove click, add/error/discount events — the exact triggers A-main-cart.js
 * reacts to) bumps the token, and the refresh's .finally() only removes
 * `is-loading` if the token is untouched. Otherwise the newer update's own
 * .finally() un-dims when IT lands.
 ********************************************/

customElements.whenDefined('l-drawer').then(() => {
	if (customElements.get('l-cart-drawer')) return

	class LTACartDrawer extends customElements.get('l-drawer') {
		constructor() {
			super()

			this._onProductAdded = this._onProductAdded.bind(this)
			this._onCartMutated = this._onCartMutated.bind(this)
			this._onLoadingOwnerChange = this._onLoadingOwnerChange.bind(this)

			// Bumped on every cart mutation. A refresh started at seq N throws its
			// response away if the seq has moved on by the time it lands.
			this._cartSeq = 0
			this._refreshInFlight = false
			this._skipNextOpenRefresh = false

			// [PAA qa3] Bumped when a theme-owned cart update starts while a refresh
			// is in flight — the refresh then no longer owns `is-loading`.
			this._loadingToken = 0
		}

		connectedCallback() {
			if (super.connectedCallback) super.connectedCallback()

			document.addEventListener('l:product:added', this._onProductAdded)

			// l-main-cart re-renders itself off these; we only track that the cart
			// moved, so an in-flight refresh knows its response is now stale.
			document.addEventListener('l:product:added', this._onCartMutated)
			document.addEventListener('l:product:updated', this._onCartMutated)
			document.addEventListener('l:cart:discount:applied', this._onCartMutated)
			this.addEventListener('l:cart:updated', this._onCartMutated)

			// [PAA qa3] The same triggers <l-main-cart> reacts to. A quantity
			// change / remove click bubbles here BEFORE its (debounced) handler adds
			// `is-loading`, and the document events start its own re-fetch cycle —
			// each one means a newer update now owns the loading state.
			this.addEventListener('change', this._onLoadingOwnerChange)
			this.addEventListener('click', this._onLoadingOwnerChange)
			document.addEventListener('l:product:added', this._onLoadingOwnerChange)
			document.addEventListener('l:product:updated', this._onLoadingOwnerChange)
			document.addEventListener('l:cart:error', this._onLoadingOwnerChange)
			document.addEventListener('l:cart:discount:applied', this._onLoadingOwnerChange)
		}

		disconnectedCallback() {
			document.removeEventListener('l:product:added', this._onProductAdded)
			document.removeEventListener('l:product:added', this._onCartMutated)
			document.removeEventListener('l:product:updated', this._onCartMutated)
			document.removeEventListener('l:cart:discount:applied', this._onCartMutated)
			this.removeEventListener('l:cart:updated', this._onCartMutated)

			this.removeEventListener('change', this._onLoadingOwnerChange)
			this.removeEventListener('click', this._onLoadingOwnerChange)
			document.removeEventListener('l:product:added', this._onLoadingOwnerChange)
			document.removeEventListener('l:product:updated', this._onLoadingOwnerChange)
			document.removeEventListener('l:cart:error', this._onLoadingOwnerChange)
			document.removeEventListener('l:cart:discount:applied', this._onLoadingOwnerChange)

			if (super.disconnectedCallback) super.disconnectedCallback()
		}

		_onCartMutated() {
			this._cartSeq += 1
		}

		/**
		 * [PAA qa3] A theme-owned cart update is starting (or about to). If our
		 * open-refresh is in flight it must NOT strip `is-loading` when it lands —
		 * the newer update's own .finally() will. Only real update triggers count:
		 * a quantity-input change / remove click inside a line item (mirrors
		 * A-main-cart.js's own filters), or the cart events it re-fetches on.
		 */
		_onLoadingOwnerChange(event) {
			if (!this._refreshInFlight) return

			if (event.type === 'change') {
				if (!(event.target instanceof Element) || !event.target.closest('[data-drawer-line-item]')) return
			} else if (event.type === 'click') {
				if (!(event.target instanceof Element) || !event.target.closest('[data-remove-line-item]')) return
			}

			this._loadingToken += 1
		}

		_onProductAdded() {
			if (this.classList.contains('is-active')) return

			// The add already triggered a fresh section fetch inside l-main-cart —
			// opening off the back of it must not fire a second, redundant one.
			this._skipNextOpenRefresh = true

			const triggerEl = document.activeElement instanceof HTMLElement ? document.activeElement : null
			this.drawer_open(triggerEl)
		}

		drawer_open(triggerEl) {
			super.drawer_open(triggerEl)

			if (this._skipNextOpenRefresh) {
				this._skipNextOpenRefresh = false
				return
			}

			this._refreshCart()
		}

		/**
		 * Pull the cart section fresh and let <l-main-cart> render it, so a cart
		 * changed out-of-band is never shown stale.
		 */
		_refreshCart() {
			const mainCartEl = this.querySelector('l-main-cart')
			if (!mainCartEl || typeof mainCartEl._updateCartData !== 'function') return

			// Guard 2: one refresh at a time.
			if (this._refreshInFlight) return

			// Guard 3a: a quantity/remove update is mid-flight — it owns the render
			// and will paint the truth when it lands. Don't race it.
			if (mainCartEl.classList.contains('is-loading')) return

			const sectionId = mainCartEl.dataset.sectionId
			if (!sectionId) return

			const cartUrl = window.LTATheme?.routes?.cart_url || '/cart'
			const seqAtRequest = this._cartSeq

			this._refreshInFlight = true
			mainCartEl.classList.add('is-loading')

			// [PAA qa3] Ownership token: this refresh added `is-loading` at token N.
			// If any theme-owned update starts while we're in flight the token moves
			// on, and .finally() leaves the class for that update to clear.
			const loadingTokenAtRequest = this._loadingToken

			fetch(`${cartUrl}?section_id=${encodeURIComponent(sectionId)}`)
				.then((res) => (res.ok ? res.text() : Promise.reject(new Error(`Cart refresh failed: ${res.status}`))))
				.then((html) => {
					// Guard 3b: the cart moved under us while we were fetching. This body
					// is already out of date — drop it rather than clobber the newer render.
					if (this._cartSeq !== seqAtRequest) return

					mainCartEl._updateCartData(html)
					this._syncDrawerCount(html)
				})
				.catch((error) => {
					// A failed refresh just leaves the existing markup in place.
					console.error(error)
				})
				.finally(() => {
					this._refreshInFlight = false

					// [PAA qa3] Only un-dim if this refresh still owns `is-loading` —
					// i.e. no quantity/remove/add update took ownership while we were in
					// flight. If one did, ITS .finally() removes the class when it lands,
					// so the UI stays dimmed until the last operation finishes.
					if (this._loadingToken === loadingTokenAtRequest) {
						mainCartEl.classList.remove('is-loading')
					}
				})
		}

		/**
		 * The "(N)" beside the drawer title lives in the drawer header, OUTSIDE
		 * <l-main-cart>, so _updateCartData never touches it. Sync it from the same
		 * HTML or a refreshed drawer would show fresh lines under a stale count.
		 */
		_syncDrawerCount(html) {
			const countEl = this.querySelector('[data-cart-count]')
			if (!countEl) return

			const newCountEl = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-count]')
			if (newCountEl) countEl.textContent = newCountEl.textContent.trim()
		}

		drawer_close() {
			const wasActive = this.classList.contains('is-active')

			super.drawer_close()

			if (!wasActive) return

			// Let product forms re-sync availability for variants changed in the cart
			const mainCartEl = this.querySelector('l-main-cart')

			document.dispatchEvent(
				new CustomEvent('l:cart:closed', {
					detail: { updatedVariants: mainCartEl?.updatedVariants || [] },
				})
			)

			if (mainCartEl) mainCartEl.updatedVariants = []
		}
	}

	customElements.define('l-cart-drawer', LTACartDrawer)
})
